import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalContent,
  Modal,
  ModalOverlay,
  ModalFooter,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';

export const EditInvoiceDueDateModal = ({
  invoiceExistingDueDate,
  updateInvoiceDueDate,
  isUpdateInvoiceDueDateLoading,
  isOpen,
  onClose,
}: {
  invoiceExistingDueDate: Util.FullDateStr;
  updateInvoiceDueDate: (newDueDate: Util.FullDateStr) => Promise<void>;
  isUpdateInvoiceDueDateLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedDueDate, setSelectedDueDate] = useState<Util.FullDateStr>(
    invoiceExistingDueDate,
  );

  const onCloseModal = useCallback(() => {
    onClose();
    setSelectedDueDate(invoiceExistingDueDate);
  }, [onClose, invoiceExistingDueDate]);

  const minDueDate = useMemo(() => {
    return commonDate.fullDateStrToDate(
      commonDate.addDays(commonDate.currentDateStr(), 1),
    );
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Due Date</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack mb="24px" w="100%" gap="8px" alignItems="flex-start">
            <Typography fontWeight="semibold">Current Due Date</Typography>
            <Typography>
              {commonDate.fullDateStrToHumanDateString(invoiceExistingDueDate)}
            </Typography>
          </VStack>
          <VStack w="100%" gap="8px" alignItems="flex-start">
            <Typography fontWeight="semibold">New Due Date</Typography>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(selectedDueDate)}
              setSelectedDate={(date: Date | null) => {
                // date must exist. this datepicker is not clearable
                if (date) {
                  const newDueDate = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  setSelectedDueDate(newDueDate);
                }
              }}
              width="100%"
              dateFormat="MMMM d, yyyy"
              minDate={minDueDate}
            />
          </VStack>
        </ModalBody>

        <ModalFooter mt="24px">
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              await updateInvoiceDueDate(selectedDueDate);
              onClose();
            }}
            isLoading={isUpdateInvoiceDueDateLoading}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
